import type { Attachment, AuditFields } from "../../types";
import type { IOperationResult } from "../types";
import { createBaseService, fromDb } from "./baseService";
import { getSupabase as getSupabaseNullable } from "../../lib/supabase";

type AttachmentBase = Omit<Attachment, "id" | keyof AuditFields>;

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

const base = createBaseService<Attachment, AttachmentBase>({
  tableName: "attachments",
  searchColumns: ["file_name"],
});

export class AttachmentService {
  static getAll = base.getAll;
  static get = base.get;
  static create = base.create;
  static update = base.update;
  static delete = base.delete;

  static async getByEntity(
    entityType: string,
    entityId: string
  ): Promise<IOperationResult<Attachment[]>> {
    try {
      if (!entityType || !entityId) {
        return { success: true, data: [] };
      }
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("attachments")
        .select("*")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .order("created_at", { ascending: false });

      if (error) return { success: false, error };
      const mapped = (data ?? []).map((row) =>
        fromDb<Attachment>(row as Record<string, unknown>)
      );
      return { success: true, data: mapped };
    } catch (error) {
      return { success: false, error };
    }
  }
}
